"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import * as React from "react";
import { cn } from "../lib/cn";

export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetClose = DialogPrimitive.Close;
export const SheetPortal = DialogPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(function SheetOverlay({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      data-aio-popup=""
      className={cn(
        "fixed inset-0 z-50 bg-fg/50 backdrop-blur-[2px]",
        "data-[state=open]:animate-[aio-overlay-in_320ms_var(--aio-sheet-ease-out)_both]",
        "data-[state=closed]:animate-[aio-overlay-out_240ms_var(--aio-sheet-ease-in)_both]",
        className,
      )}
      {...props}
    />
  );
});

const sheetVariants = cva(
  "fixed z-50 gap-4 bg-bg-elevated p-6 shadow-2xl will-change-transform",
  {
    variants: {
      side: {
        top: cn(
          "inset-x-0 top-0 border-b border-border",
          "data-[state=open]:animate-[aio-sheet-in-top_360ms_var(--aio-sheet-ease-out)_both]",
          "data-[state=closed]:animate-[aio-sheet-out-top_280ms_var(--aio-sheet-ease-in)_both]",
        ),
        bottom: cn(
          "inset-x-0 bottom-0 border-t border-border",
          "data-[state=open]:animate-[aio-sheet-in-bottom_360ms_var(--aio-sheet-ease-out)_both]",
          "data-[state=closed]:animate-[aio-sheet-out-bottom_280ms_var(--aio-sheet-ease-in)_both]",
        ),
        left: cn(
          "inset-y-0 left-0 h-full w-3/4 border-r border-border sm:max-w-sm",
          "data-[state=open]:animate-[aio-sheet-in-left_400ms_var(--aio-sheet-ease-out)_both]",
          "data-[state=closed]:animate-[aio-sheet-out-left_300ms_var(--aio-sheet-ease-in)_both]",
        ),
        right: cn(
          "inset-y-0 right-0 h-full w-3/4 border-l border-border sm:max-w-sm",
          "data-[state=open]:animate-[aio-sheet-in-right_400ms_var(--aio-sheet-ease-out)_both]",
          "data-[state=closed]:animate-[aio-sheet-out-right_300ms_var(--aio-sheet-ease-in)_both]",
        ),
      },
    },
    defaultVariants: { side: "right" },
  },
);

export interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof sheetVariants> {}

/**
 * Side-anchored sheet content. For accessibility, always include a
 * `<SheetTitle>` and either a `<SheetDescription>` OR explicitly set
 * `aria-describedby` on this content. Radix warns at runtime when neither
 * is present.
 */
export const SheetContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(function SheetContent({ side = "right", className, children, ...props }, ref) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <DialogPrimitive.Content
        ref={ref}
        data-aio-popup=""
        className={cn(sheetVariants({ side }), className)}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
          <X className="size-4" aria-hidden="true" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </SheetPortal>
  );
});

export function SheetHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("flex flex-col gap-1.5 text-center sm:text-left", className)}
      {...props}
    />
  );
}

export function SheetFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:space-x-2",
        className,
      )}
      {...props}
    />
  );
}

export const SheetTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(function SheetTitle({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn("text-lg font-semibold text-fg", className)}
      {...props}
    />
  );
});

export const SheetDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(function SheetDescription({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn("text-sm text-fg-muted", className)}
      {...props}
    />
  );
});
